import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [pending2FA, setPending2FA] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      setLoading(false);
      return;
    }
    
    // Keep loading true until we have the full profile
    setLoading(true);
    
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      
      // Cache for sidebar and header
      localStorage.setItem('user_full_name', res.data.full_name || '');
      localStorage.setItem('user_company', res.data.company_name || '');
      localStorage.setItem('user_email', res.data.email || '');
      localStorage.setItem('user_id', res.data.id || '');
    } catch {
      // Token invalid or expired
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback((accessToken, refreshToken, email) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user_email', email);
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
  // Only remove auth-related items, not theme
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_full_name');
    localStorage.removeItem('user_company');
    localStorage.removeItem('user_id');
    setUser(null);
  }, []);

  const refreshUser = async () => {
    try {
      const res = await api.get('/api/auth/me');
      if (res.data) {
        setUser(res.data);
        localStorage.setItem('user_full_name', res.data.full_name || '');
        localStorage.setItem('user_company', res.data.company_name || '');
        localStorage.setItem('user_email', res.data.email || '');
        localStorage.setItem('user_id', res.data.id || '');
        localStorage.setItem('user_plan', res.data.plan || 'free');
      }
    } catch (e) {
      console.error('Failed to refresh user', e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, pending2FA, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);